import { useState } from "react";
import { Check, ChevronRight, Sparkles } from "lucide-react";
import { useCheckout } from "#/api/billing";
import type { CheckoutInterval } from "@myapp/shared";
import { LogoMark } from "#/components/LogoMark";

function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14] hover:shadow-[0_0_24px_rgba(180,160,220,0.2)] disabled:opacity-40 disabled:hover:shadow-none"
    >
      {children}
    </button>
  );
}

function PlanCard({
  title,
  price,
  subtitle,
  selected,
  onSelect,
}: {
  title: string;
  price: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-2xl border p-5 text-left transition-all ${
        selected
          ? "border-primary/70 bg-primary/10"
          : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="mt-1 font-serif text-2xl text-primary">{price}</p>
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div
          className={`mt-1 h-5 w-5 flex-shrink-0 rounded-full border-2 ${
            selected ? "border-primary bg-primary" : "border-white/30"
          }`}
        />
      </div>
    </button>
  );
}

export function UpgradeSlide({
  nextFreeAtLabel,
  onWait,
}: {
  nextFreeAtLabel?: string | null;
  onWait: () => void;
}) {
  const checkout = useCheckout();
  const [interval, setInterval] = useState<CheckoutInterval>("yearly");

  const benefits = [
    "No weekly wait — start sessions anytime",
    "Up to 50 sessions per day",
    "Build your personal belief map over time",
    "Revisit past breakthroughs anytime",
  ];

  return (
    <section className="flex min-h-screen w-full snap-start flex-col items-center justify-center px-6 py-24">
      <div className="mb-6">
        <LogoMark size="lg" />
      </div>

      <p className="mb-3 text-xs uppercase tracking-widest text-primary/80">
        Continue your journey
      </p>

      <h2 className="mb-4 max-w-lg text-center font-serif text-3xl font-light leading-tight text-foreground md:text-4xl">
        You&apos;ve used this week&apos;s free session
      </h2>

      <p className="mb-6 max-w-md text-center text-sm leading-relaxed text-muted-foreground">
        {nextFreeAtLabel
          ? `Your next free session arrives ${nextFreeAtLabel}. Or go Premium and transform fears whenever they knock.`
          : "Go Premium and transform fears whenever they knock."}
      </p>

      <div className="mb-6 w-full max-w-md space-y-2">
        {benefits.map((benefit) => (
          <div
            key={benefit}
            className="flex items-start gap-3 text-sm text-foreground/90"
          >
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>{benefit}</span>
          </div>
        ))}
      </div>

      <div className="mb-6 w-full max-w-md space-y-3">
        <PlanCard
          title="Yearly"
          price="$49.99 / year"
          subtitle="Save 48% — about $4.17/month"
          selected={interval === "yearly"}
          onSelect={() => setInterval("yearly")}
        />
        <PlanCard
          title="Monthly"
          price="$7.99 / month"
          subtitle="Cancel anytime"
          selected={interval === "monthly"}
          onSelect={() => setInterval("monthly")}
        />
      </div>

      <PrimaryButton
        onClick={() => checkout.mutate(interval)}
        disabled={checkout.isPending}
      >
        <Sparkles className="h-4 w-4" />
        {checkout.isPending ? "Opening checkout..." : "Go Premium"}
        <ChevronRight className="h-4 w-4" />
      </PrimaryButton>

      {checkout.isError && (
        <p className="mt-4 max-w-sm text-center text-xs text-destructive-foreground">
          {checkout.error.message}
        </p>
      )}

      <button
        onClick={onWait}
        className="mt-6 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        I&apos;ll wait for my free session
      </button>
    </section>
  );
}
